import { Col, Form, Input, InputNumber, Radio, Row, Select, Switch } from "antd";
import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { PERMISSIONS } from "../../../constants/permission";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { IMasterDataSupplier } from "../../../interfaces/master-data";
import { IRequestSupplierGeneralInfo } from "../../../interfaces/supplier";
import AlternativeNameInput from "./alternative-name-form";
import { useAuth } from "../../../context/AuthContext";

const { TextArea } = Input;

interface GeneralInfoFormProps {
  form: any;
  initialValues: IRequestSupplierGeneralInfo;
  masterData: IMasterDataSupplier;
}

const filterSelectOption = (input: string, option?: { label: string }) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

const generateOptions = (list: any[], labelFn: (item: any) => string, valueKey = "code") =>
  list.map((item) => ({
    value: item[valueKey],
    label: labelFn(item),
  }));

const FormSelect = ({
  name,
  label,
  options,
  rules,
  required,
  disabled,
}: {
  name: string;
  label: string;
  options: any[];
  rules?: any[];
  required?: boolean;
  disabled?: boolean;
}) => (
  <Form.Item
    name={name}
    label={label}
    labelCol={{ flex: "0 0 180px" }}
    rules={required ? [{ required: true, message: "Complete this required field." }] : rules}
  >
    <Select
      showSearch
      allowClear
      filterOption={filterSelectOption}
      options={options}
      placeholder="Select"
      {...(disabled && { disabled: true })}
    />
  </Form.Item>
);

const GeneralInfoForm: React.FC<GeneralInfoFormProps> = ({ form, initialValues, masterData }) => {
  const {
    countries = [],
    paymentMethods = [],
    currencies = [],
    productHead = [],
    productOwner = [],
    apEscalation = [],
    apOwner = [],
  } = masterData;

  const { id } = useParams();
  const isEditMode = !!id;
  const { user } = useAuth();
  const { roles = [] } = user || {};
  const isAdmin = roles[0]?.name === "Admin";

  const countryOptions = useMemo(() => generateOptions(countries, (i) => i.label), [countries]);
  const paymentMethodOptions = useMemo(() => generateOptions(paymentMethods, (i) => i.label), [paymentMethods]);
  const currencyOptions = useMemo(() => generateOptions(currencies, (i) => `${i.code} - ${i.label}`), [currencies]);
  const productOwnerOptions = useMemo(
    () => generateOptions(productOwner, (i) => `${i.firstName} ${i.lastName}`, "id"),
    [productOwner]
  );
  const productHeadOptions = useMemo(
    () => generateOptions(productHead, (i) => `${i.firstName} ${i.lastName}`, "id"),
    [productHead]
  );
  const apOwnerOptions = useMemo(
    () => generateOptions(apOwner, (i) => `${i.firstName} ${i.lastName}`, "id"),
    [apOwner]
  );
  const apEscalationOptions = useMemo(
    () => generateOptions(apEscalation, (i) => `${i.firstName} ${i.lastName}`, "id"),
    [apEscalation]
  );

  const hasSupplierEditor = useHasPermission([PERMISSIONS.SUPPLIER_EDITOR]);

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues, form]);

  const onChangeForm = ({ isCreditLimit, isPartOfAGroup, isCreditSupplier, phoneNumber }: any) => {
    const updates: any = {};
    if (isCreditLimit === false || isCreditSupplier === false) {
      updates.isCreditLimit = false;
      updates.creditLimitValue = undefined;
      updates.creditLimitNote = undefined;
    }
    if (isPartOfAGroup === false) {
      updates.groupName = undefined;
    }
    if (phoneNumber === "") {
      updates.phoneNumber = undefined;
    }
    if (Object.keys(updates).length) {
      form.setFieldsValue(updates);
    }
  };

  return (
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ flex: "0 0 180px" }} // Thêm dòng này
      onValuesChange={onChangeForm}
      disabled={!hasSupplierEditor}
    >
      <Row gutter={24}>
        <Col span={16}>
          {/* Basic Info */}
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Supplier Code"
                name="supplierCode"
                rules={[{ required: true, message: "Complete this required field." }]}
              >
                <Input disabled={isEditMode || !hasSupplierEditor} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Supplier Name"
                name="supplierName"
                rules={[{ required: true, message: "Complete this required field." }]}
              >
                <Input />
              </Form.Item>
            </Col>
            {/* Supplier Address nằm ở hàng riêng nhưng vẫn trong Col span={16} */}
            <Col span={24}>
              <Form.Item label="Supplier Address" name="supplierAddress">
                <Input />
              </Form.Item>
            </Col>

            {/* Country & RFI */}
            <Col span={12}>
              <FormSelect name="country" label="Country" options={countryOptions} required />
            </Col>
            <Col span={12}>
              <Form.Item
                name="isRfi21FormRequired"
                label="RFI-21 Form Required"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>

            {/* Group */}
            <Col span={12}>
              <Form.Item name="isPartOfAGroup" label="Part of Group" valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => (
                  <Form.Item label="Group Name" name="groupName">
                    <Input disabled={!getFieldValue("isPartOfAGroup") || !hasSupplierEditor} />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>

            {/* Contact Info */}
            <Col span={12}>
              <Form.Item label="Contact Name" name="contactName">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Phone"
                name="phoneNumber"
                rules={[
                  {
                    pattern: /^\+?\d{9,15}$/,
                    message: "Phone number must be 10 to 15 digits",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>

            {/* Email Fields */}
            {["contactEmail1", "contactEmail2"].map((field, index) => (
              <Col span={12} key={field}>
                <Form.Item
                  label={`Email ${index + 1}`}
                  name={field}
                  rules={
                    index === 0
                      ? [
                          { required: true, message: "Complete this required field." },
                          { type: "email", message: "Please enter a valid email address" },
                        ]
                      : [{ type: "email", message: "Please enter a valid email address" }]
                  }
                >
                  <Input />
                </Form.Item>
              </Col>
            ))}

            {/* User Selects */}
            <Col span={12}>
              <FormSelect name="productHead" label="Product Head" options={productHeadOptions} required />
            </Col>
            <Col span={12}>
              <FormSelect name="productOwner" label="Product Owner" options={productOwnerOptions} required />
            </Col>
            <Col span={12}>
              <FormSelect name="apOwner" label="AP Owner" options={apOwnerOptions} required />
            </Col>
            <Col span={12}>
              <FormSelect name="apEscalation" label="AP Escalation" options={apEscalationOptions} required />
            </Col>
            <Col span={12}>
              <FormSelect name="paymentMethod" label="Payment Method" options={paymentMethodOptions} required />
            </Col>
            <Col span={12}>
              <FormSelect name="currency" label="Currency" options={currencyOptions} required disabled={!isAdmin && isEditMode} />
            </Col>
            <Col span={12}>
              <Form.Item
                name="isAllowZeroAmountInvoice"
                label="Allow 0 Amount Invoice"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isExcludeReport"
                label="Exclude from Dashboard"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col span={8}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item colon={false} style={{ marginBottom: "24px" }}>
                <AlternativeNameInput form={form} disabled={!hasSupplierEditor} />
              </Form.Item>
            </Col>

            <Col span={24} className="mt-2">
              <Form.Item
                label={`Invoice sender email`}
                name={"contactEmail3"}
                rules={[{ type: "email", message: "Please enter a valid email address" }]}
              >
                <Input />
              </Form.Item>
            </Col>

            {/* Credit Info */}
            <Col span={24}>
              <Form.Item name="isCreditSupplier" label="Credit Supplier" valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
            </Col>

            <Form.Item shouldUpdate noStyle>
              {({ getFieldValue }) => (
                <Col span={24}>
                  <Form.Item label="Credit Limit" name="isCreditLimit" initialValue={false}>
                    <Radio.Group disabled={!getFieldValue("isCreditSupplier") || !hasSupplierEditor}>
                      <Radio value={false}>Unlimited</Radio>
                      <Radio value={true}>Amount</Radio>
                    </Radio.Group>
                  </Form.Item>

                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="creditLimitValue"
                        label={" "}
                        colon={false}
                        rules={[
                          {
                            required: getFieldValue("isCreditLimit"),
                            message: "Complete this required field.",
                          },
                        ]}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          placeholder="Value"
                          disabled={!getFieldValue("isCreditLimit") || !hasSupplierEditor}
                          min={1}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>

                      <Form.Item name="creditLimitNote" label={" "} colon={false}>
                        <TextArea
                          placeholder="Note"
                          rows={2}
                          disabled={!getFieldValue("isCreditLimit") || !hasSupplierEditor}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Col>
              )}
            </Form.Item>
          </Row>
        </Col>
      </Row>
    </Form>
  );
};

export default GeneralInfoForm;
